import {
  DataPointValue,
  IMeasureMetadata,
  MeasureFormatter,
} from '@principle-theorem/principle-core/interfaces';
import {
  DAY_MONTH_YEAR_FORMAT,
  TIME_FORMAT,
  isArray,
  toInt,
} from '@principle-theorem/shared';
import { isBoolean, isString, isUndefined } from 'lodash';
import * as moment from 'moment-timezone';

export type ValueFormatterFn = (value: DataPointValue) => string;
const defaultDateFormat = `${DAY_MONTH_YEAR_FORMAT} - ${TIME_FORMAT}`;

export class ValueFormatter {
  static readonly locale = 'en-AU';

  static getFormatter(
    formatter?: MeasureFormatter,
    formatterValue?: IMeasureMetadata['formatterValue']
  ): ValueFormatterFn {
    return (value) => ValueFormatter.format(value, formatter, formatterValue);
  }

  static format(
    value: DataPointValue,
    formatter?: MeasureFormatter,
    formatterValue?: IMeasureMetadata['formatterValue']
  ): string {
    if (isArray(value)) {
      return value
        .map((dataPoint) =>
          ValueFormatter.format(
            dataPoint as DataPointValue,
            formatter,
            formatterValue
          )
        )
        .join(', ');
    }

    if (formatterValue && !isString(formatterValue)) {
      return formatterValue(value).toString();
    }

    switch (formatter) {
      case MeasureFormatter.Boolean:
        return ValueFormatter.formatBoolean(value);
      case MeasureFormatter.Percentage:
        return ValueFormatter.formatPercentage(value);
      case MeasureFormatter.Currency:
        return ValueFormatter.formatCurrency(value);
      case MeasureFormatter.Number:
        return ValueFormatter.formatNumber(value);
      case MeasureFormatter.Minutes:
        return ValueFormatter.formatMins(value);
      case MeasureFormatter.Prefix:
        return ValueFormatter.formatPrefix(value, formatterValue);
      case MeasureFormatter.Suffix:
        return ValueFormatter.formatSuffix(value, formatterValue);
      case MeasureFormatter.Timestamp:
        return ValueFormatter.formatTimestamp(value, formatterValue);
      case MeasureFormatter.Day:
        return ValueFormatter.formatTimestamp(value, DAY_MONTH_YEAR_FORMAT);
      case MeasureFormatter.Time:
        return ValueFormatter.formatTimestamp(value, TIME_FORMAT);
      default:
        return value?.toString() || '';
    }
  }

  static formatBoolean(value?: string | number | boolean): string {
    return value ? 'Yes' : 'No';
  }

  static formatCurrency(value: DataPointValue, decimals: number = 2): string {
    const formatter = new Intl.NumberFormat(ValueFormatter.locale, {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: decimals,
    });
    return formatter.format(ValueFormatter.coerceToNumber(value));
  }

  static formatPercentage(value: DataPointValue): string {
    const formatter = new Intl.NumberFormat(ValueFormatter.locale, {
      style: 'percent',
      maximumFractionDigits: 2,
    });
    return formatter.format(ValueFormatter.coerceToNumber(value));
  }

  static formatNumber(value: DataPointValue): string {
    return ValueFormatter.coerceToNumber(value).toLocaleString();
  }

  static formatPrefix(value: DataPointValue, formatterValue?: string): string {
    const num = ValueFormatter.coerceToNumber(value);
    // When the value is a non-numeric label (e.g. "No DOB"), return it as-is
    // instead of producing "prefix NaN"
    if (isNaN(num)) {
      return isString(value) ? value : '';
    }
    if (!formatterValue) {
      // eslint-disable-next-line no-console
      console.error('No prefix value');
      return num.toString();
    }
    return `${formatterValue} ${num}`;
  }

  static formatSuffix(value: DataPointValue, formatterValue?: string): string {
    const num = ValueFormatter.coerceToNumber(value);
    // When the value is a non-numeric label (e.g. "No DOB"), return it as-is
    // instead of producing "NaN suffix"
    if (isNaN(num)) {
      return isString(value) ? value : '';
    }
    if (!formatterValue) {
      // eslint-disable-next-line no-console
      console.error('No suffix value');
      return num.toString();
    }
    return `${num} ${formatterValue}`;
  }

  static formatMins(value: DataPointValue): string {
    return ValueFormatter.formatSuffix(toInt(value), 'mins');
  }

  static formatTimestamp(
    value: DataPointValue,
    formatterValue: string = defaultDateFormat
  ): string {
    if (!value) {
      return '';
    }
    if (!isString(value)) {
      // eslint-disable-next-line no-console
      console.error('Cannot format timestamp', value);
      return value.toString();
    }
    return moment(value).format(formatterValue);
  }

  static coerceToNumber(value: DataPointValue): number {
    if (isString(value)) {
      return Number(value);
    }
    if (isBoolean(value)) {
      return value ? 1 : 0;
    }
    if (isUndefined(value)) {
      return 0;
    }
    return value;
  }
}
